import React, { useState, useEffect, useRef, Fragment } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Button } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { Tooltip } from "components/tooltip";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { PcnService } from "services/PcnService";
import { PcnContraventionCodeModal } from "pages/pcn/admin/settings/PcnContraventionCodeModal";

const requiredAction = "Internal";
const pageSize = 50;
const debounceTimeout = 750;
const badRequestCode = 400;
const generalErrorMessage = "An error occurred while loading contravention codes. Please try again.";

const defaultModel = {
    id: 0,
    contraventionCode: "",
    contraventionDescription: "",
    responsibilityKey: null
};

const sortableHeaders = [
    { headerName: "Contravention Code", sortName: "CONTRAVENTIONCODE" },
    { headerName: "Description", sortName: "CONTRAVENTIONDESCRIPTION" },
    { headerName: "Responsibility", sortName: "RESPONSIBILITYKEY" }
];

const tableHeaders = ["Contravention Code", "Description", "Responsibility", "Actions"];

export function PcnContraventionCodesTab() {
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [loading, setLoading] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const [responsibilities, setResponsibilities] = useState([]);
    const [rawCodes, setRawCodes] = useState([]);
    // Keeps the whole listing in a loading state until the responsibility lookup has loaded,
    // so rows never flash with an unresolved "-" responsibility before the names are available.
    const [initialLoad, setInitialLoad] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedCode, setSelectedCode] = useState(defaultModel);

    const inputSearchRef = useRef(null);

    // Responsibilities power both the listing name lookup and the modal dropdown.
    useEffect(loadResponsibilities, []);

    function loadResponsibilities() {
        PcnService.getOptions()
            .then((options) => {
                setResponsibilities(options?.responsibilities ?? []);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load responsibilities. Please refresh the page.");
            })
            .finally(() => {
                setInitialLoad(false);
            });
    }

    function getResponsibilityName(responsibilityKey) {
        return responsibilities.find((responsibility) => responsibility.key === responsibilityKey)?.name ?? "-";
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function addContraventionCode() {
        setIsModalUpdate(false);
        setSelectedCode(defaultModel);
        setShowModal(true);
    }

    function onEditContraventionCode(id) {
        setModalLoading(true);
        setIsModalUpdate(true);
        setShowModal(true);

        PcnService.getContraventionCode(id)
            .then((details) => {
                setSelectedCode(details);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load the contravention code. Please try again.");
                onCloseModal();
            })
            .finally(() => {
                setModalLoading(false);
            });
    }

    function onCloseModal() {
        setShowModal(false);
        setSelectedCode(defaultModel);
        setIsModalUpdate(false);
    }

    function onSaveModal() {
        onCloseModal();
        fetchData();
    }

    function fetchData() {
        setLoading(true);

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            searchTerm: debouncedSearchTerm,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir
        };

        PcnService.searchContraventionCodes(searchRequest)
            .then((response) => {
                setRawCodes(response.data ?? []);
                setTotalRecords(response.totalCount);
            }, (error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, generalErrorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, [debouncedSearchTerm, pageNumber, sortedBy, sortedDir]);

    // Re-map the display rows whenever the data or the responsibility lookup changes, so the
    // responsibility name resolves without triggering a second search request on initial load.
    useEffect(() => {
        const rows = rawCodes.map((row) => {
            return [
                row.contraventionCode,
                row.contraventionDescription,
                getResponsibilityName(row.responsibilityKey),
                <div key={`${row.id}-actions`} className="button-row-small inline-items">
                    <Tooltip content="Edit contravention code">
                        <Button
                            onClick={() => onEditContraventionCode(row.id)}
                            icon="edit"
                            iconOnly={true}
                            minimal={true}
                            large={false}
                        />
                    </Tooltip>
                </div>
            ];
        });

        setTableRows(rows);
    }, [rawCodes, responsibilities]);

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="spacer-bottom">
                        <Button intent="primary" text="Add new contravention code" id="add-contravention-code" onClick={addContraventionCode} loading={loading} />
                    </div>

                    <FormTextInput
                        inputRef={inputSearchRef}
                        placeholder="Search contravention codes"
                        disabled={loading && inputSearchRef.current !== document?.activeElement}
                        onChange={onSearchChange}
                        value={searchTerm}
                        large
                        icon="search"
                        id="contravention-code-search-field"
                        fill
                    />

                    <ListingTable
                        id="listing-table-contravention-codes"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loading || initialLoad}
                        onPagingChange={onPagingChange}
                        startPage={pageNumber}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        showPerPage={false}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onSort}
                        noDataMessage="No contravention codes to display"
                    />

                    <ShowHide
                        evaluator={showModal}
                        show={(
                            <PcnContraventionCodeModal
                                showModal={showModal}
                                modalTitle={isModalUpdate ? "Update Contravention Code" : "Add New Contravention Code"}
                                isUpdate={isModalUpdate}
                                loading={modalLoading}
                                responsibilities={responsibilities.map((responsibility) => ({ id: responsibility.key, name: responsibility.name }))}
                                onSave={onSaveModal}
                                onCloseModal={onCloseModal}
                                contraventionCodeDetails={selectedCode}
                            />
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
